import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { ApiError, api, unwrap } from "@/client/lib/api";
import { ensureTenantData } from "@/client/lib/session";
import type { InvoiceDocumentData } from "@/features/invoices/components/invoice-document";
import type { documentOnTheWire } from "@/features/invoices/lib/invoice-wire";

/** The front of every invoice key: the list's own key, and of each open invoice. */
export const invoicesKey = ["invoices"] as const;

/**
 * Drafts this tab made with New invoice and has not left yet, with the note each started with; only
 * these may be dropped on leave.
 */
export const newDrafts = new Map<string, string | null>();

/** One draft's saves run in order under this scope. */
export const draftScope = (invoiceId: string) => `invoice-draft:${invoiceId}`;

/** Whether a save was made to the draft in this tab, including one still in flight. */
export const draftEdited = (queryClient: QueryClient, invoiceId: string) =>
  queryClient.getMutationCache().findAll({
    predicate: (save) => save.options.scope?.id === draftScope(invoiceId),
  }).length > 0;

export const invoiceQuery = (invoiceId: string) =>
  queryOptions({
    queryKey: [...invoicesKey, invoiceId],
    queryFn: () =>
      unwrap(api.invoices[":id"].$get({ param: { id: invoiceId } })),
  });

export type InvoiceDetail = Awaited<
  ReturnType<NonNullable<ReturnType<typeof invoiceQuery>["queryFn"]>>
>;

/** What the Add item dialog suggests, which follows the draft's client and so its currency. */
export const pastLinesQuery = (
  invoice: Pick<InvoiceDetail, "id" | "clientId" | "currency">,
) =>
  queryOptions({
    queryKey: [
      ...invoicesKey,
      invoice.id,
      "past-lines",
      invoice.clientId,
      invoice.currency,
    ],
    queryFn: () =>
      unwrap(
        api.invoices[":id"]["past-lines"].$get({ param: { id: invoice.id } }),
      ).then(({ items }) => items),
  });

export type PastLine = Awaited<
  ReturnType<NonNullable<ReturnType<typeof pastLinesQuery>["queryFn"]>>
>[number];

/** The company's details as a draft's preview prints them. */
export const invoiceSettingsQuery = queryOptions({
  queryKey: ["invoice-settings"],
  queryFn: () => unwrap(api.invoices.settings.$get()),
});

export type InvoiceSettings = Awaited<
  ReturnType<NonNullable<(typeof invoiceSettingsQuery)["queryFn"]>>
>;

/** The number the next finalised invoice gets, which the confirmations name. */
export const numberingQuery = queryOptions({
  queryKey: [...invoicesKey, "numbering"],
  queryFn: () => unwrap(api.invoices.numbering.$get()),
});

export type NumberingShown = Awaited<
  ReturnType<NonNullable<(typeof numberingQuery)["queryFn"]>>
>;

/** Another company's invoice, a deleted draft and a malformed id all read as not found. */
export async function ensureInvoice(
  queryClient: QueryClient,
  invoiceId: string,
): Promise<boolean> {
  try {
    await ensureTenantData(queryClient, invoiceQuery(invoiceId));
    return true;
  } catch (failure) {
    if (
      failure instanceof ApiError &&
      (failure.code === "NOT_FOUND" || failure.code === "VALIDATION_FAILED")
    ) {
      return false;
    }
    throw failure;
  }
}

/** Money comes as decimal strings and is cached that way; the document prints bigints. */
function documentFromTheWire(
  wire: ReturnType<typeof documentOnTheWire>,
): InvoiceDocumentData {
  return {
    ...wire,
    lines: wire.lines.map((line) => ({
      ...line,
      rateMinor: BigInt(line.rateMinor),
      amountMinor: BigInt(line.amountMinor),
    })),
    taxes: wire.taxes.map((tax) => ({
      ...tax,
      amountMinor: BigInt(tax.amountMinor),
    })),
    subtotalMinor: BigInt(wire.subtotalMinor),
    totalMinor: BigInt(wire.totalMinor),
    paidMinor: BigInt(wire.paidMinor),
    writtenOffMinor: BigInt(wire.writtenOffMinor),
    amountDueMinor: BigInt(wire.amountDueMinor),
  };
}

export const invoiceDocumentQuery = (id: string) =>
  queryOptions({
    queryKey: ["invoices", id, "document"],
    queryFn: () => unwrap(api.invoices[":id"].document.$get({ param: { id } })),
    select: documentFromTheWire,
  });

const readPayments = (id: string) =>
  unwrap(api.invoices[":id"].payments.$get({ param: { id } })).then(
    ({ items }) => items,
  );

export type InvoicePayment = Awaited<ReturnType<typeof readPayments>>[number];

export const invoicePaymentsQuery = (id: string) =>
  queryOptions({
    queryKey: ["invoices", id, "payments"],
    queryFn: () => readPayments(id),
  });

const readActivity = (id: string) =>
  unwrap(api.invoices[":id"].activity.$get({ param: { id } })).then(
    ({ items }) => items,
  );

export type InvoiceEvent = Awaited<ReturnType<typeof readActivity>>[number];

export const invoiceActivityQuery = (id: string) =>
  queryOptions({
    queryKey: ["invoices", id, "activity"],
    queryFn: () => readActivity(id),
  });

const readLink = (token: string) =>
  unwrap(api.i[":token"].$get({ param: { token } }));

const linkFromTheWire = (linked: Awaited<ReturnType<typeof readLink>>) => ({
  ...linked,
  document: documentFromTheWire(linked.document),
});

export type LinkedInvoice = ReturnType<typeof linkFromTheWire>;

/** The client page: no session, the token is the credential. */
export const invoiceLinkQuery = (token: string) =>
  queryOptions({
    queryKey: ["invoice-link", token],
    queryFn: () => readLink(token),
    select: linkFromTheWire,
    retry: false,
  });

/** What the issued page draws beside the invoice, read before it opens so it opens whole. */
export function ensureIssuedInvoice(
  queryClient: QueryClient,
  invoiceId: string,
): Promise<unknown> {
  return Promise.all([
    ensureTenantData(queryClient, invoiceDocumentQuery(invoiceId)),
    ensureTenantData(queryClient, invoicePaymentsQuery(invoiceId)),
    ensureTenantData(queryClient, invoiceActivityQuery(invoiceId)),
  ]);
}
